import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { competitions, sports } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { SPORTSBOOK_ROUTE_WITH_CATEGORY } from 'utils/sportsbook/route.structure';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand } from 'services/get-data-level';
import { unsubscribeCommand } from 'services/get-swarm-data';
import { SportItem, SportItemSkeleton } from './SportItem';
import { NoItem } from 'components/Shared/NoItem';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setTournamentSportIds } from 'store/actions';
import { setGameInfo } from 'store/actions/game-data';
import { getTournamentSportIds } from 'store/selectors/sport-data';
import './index.less';

type TTopMatchesSportListProps = {
  handleRedirect?: (url: string) => void;
};

export const TopMatchesSportList = ({
  handleRedirect
}: TTopMatchesSportListProps): ReactElement => {
  const configs = useSportsbookConfigs();

  const { t } = useTranslation();
  const { mounted } = useMounted();

  const dispatch = useDispatch();
  const history = useHistory();
  const routeParams = useSbRouteMatch(SPORTSBOOK_ROUTE_WITH_CATEGORY);
  const tournamentSportIds = useSelector(getTournamentSportIds);

  const sportInRoute = routeParams?.params.sport;

  const [sportsData, setSportsData] = useState<Sport[] | null | undefined>(
    null
  );

  useEffect(() => {
    const where: Record<string, any> = {
      game: {
        promoted: true,
        type: {
          '@in': [0, 2]
        }
      }
    };

    if (configs.sportType) {
      where.sport = {
        type: {
          '@in': getLivePreMatchSportTypes(configs.sportType)
        }
      };
    }

    const what: {
      [key: string]: Array<string>;
    } = {
      sport: sports.sport,
      competition: [...competitions.competition, 'favorite_order'],
      region: ['alias']
    };

    const Rid = RidGenerator.gForSubscribe('TopMatchesSportsList');

    setSportsData([]);
    executeCommand(
      Rid,
      what,
      where,
      (resp: { sport: Record<string, Sport> }) => {
        if (mounted.current && resp.sport && Object.keys(resp.sport).length) {
          const orderedSports = sortByOrder(Object.values(resp.sport));
          setSportsData(orderedSports);
        } else {
          setSportsData(undefined);
        }
      }
    );

    return () => {
      unsubscribeCommand(Rid);
    };
  }, []);

  const handleSportSelect = (sport: Sport) => {
    if (
      sport?.alias === sportInRoute &&
      routeParams.params?.category === ENotRealSports.topMatches
    ) {
      return;
    }

    const regions = Object.values(sport?.region || {})?.[0];
    const competiton = Object.values(regions.competition || {})?.[0]?.id;

    const redirectUrl = `${mountPoint(routeParams.path)}/${
      ENotRealSports.topMatches
    }${
      isMobile()
        ? ''
        : `/${sport.alias}${regions ? `/${regions.alias}` : ''}${
            competiton ? `/${competiton}` : ''
          }`
    }`;

    const isInPrematch = document.querySelector('.prematch__type-page');

    if (!tournamentSportIds?.includes(sport.id || null)) {
      dispatch(setTournamentSportIds(null));
    }

    if (handleRedirect && !isInPrematch) {
      const redirectUrlBasePath = redirectUrl.split('/')[1];
      const basePath = SpringConfigs.PAGE_URLS.prematch.replace(
        `${window.location.origin}/`,
        ''
      );

      if (redirectUrlBasePath !== basePath) {
        const [, filteredUrl] = redirectUrl.split(
          SpringConfigs.SPORTSBOOK_MOUNT_PATH
        );

        handleRedirect(`/${SpringConfigs.SPORTSBOOK_MOUNT_PATH + filteredUrl}`);

        return;
      }
    }

    if (routeParams) {
      dispatch(setGameInfo(null));
      history.push(redirectUrl);
    }
  };

  if (sportsData === undefined) {
    return (
      <NoItem
        className="topMatchesSportsListWrapper__noItem"
        title={t('sportsbook.noGame')}
        text={t('sportsbook.noGameText')}
        size="medium"
      />
    );
  }

  return (
    <div className="topMatchesSportsListWrapper">
      {sportsData?.length ? (
        sportsData?.map(sport => (
          <SportItem
            key={sport.id}
            sport={sport}
            isActive={
              sport.alias === sportInRoute &&
              routeParams.params?.category === ENotRealSports.topMatches
            }
            onClick={handleSportSelect}
          />
        ))
      ) : (
        <SportItemSkeleton skeletonCount={8} />
      )}
    </div>
  );
};
